import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Avatar,
  Button,
  Divider,
  Chip,
  Stack,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Person from '@mui/icons-material/Person';
import CheckCircle from '@mui/icons-material/CheckCircle';
import AccountBalanceWallet from '@mui/icons-material/AccountBalanceWallet';
import Edit from '@mui/icons-material/Edit';
import LocationOn from '@mui/icons-material/LocationOn';
import History from '@mui/icons-material/History';
import CallMade from '@mui/icons-material/CallMade';
import Lock from '@mui/icons-material/Lock';
import LogoutIcon from '@mui/icons-material/Logout';
import { authService, performLogout } from '../services/authService';
import { addressService } from '../services/addressService';
import { walletService } from '../services/walletService';

export default function UserProfile({ onNavigate }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  // Profile dialog state
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [editableProfile, setEditableProfile] = useState({
    fullName: '',
    email: '',
    contactNumber: '',
  });
  const [profileUpdateLoading, setProfileUpdateLoading] = useState(false);
  const [profileSuccessMsg, setProfileSuccessMsg] = useState('');
  const [profileUpdateError, setProfileUpdateError] = useState('');

  // Address state
  const [address, setAddress] = useState({
    line: '',
    city: '',
    state: '',
    pincode: '',
  });
  const [isAddressDialogOpen, setIsAddressDialogOpen] = useState(false);
  const [tempAddress, setTempAddress] = useState({
    line: '',
    city: '',
    state: '',
    pincode: '',
  });
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressSuccessMsg, setAddressSuccessMsg] = useState('');
  const [addressError, setAddressError] = useState('');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  // Wallet Summary state
  const [walletSummary, setWalletSummary] = useState({
    totalBalance: 0,
    totalCashback: 0,
    totalPendingAmount: 0,
    totalTransactions: 0,
  });
  const [walletLoading, setWalletLoading] = useState(false);

  const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const isLoggedIn = !!token && !!userId;

  useEffect(() => {
    if (!isLoggedIn) {
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      try {
        setLoading(true);
        setErrorMsg('');
        const response = await authService.getUserProfile(userId);
        if (response && response.success) {
          setProfile(response.data);
          if (response.data.isPromotionalOptIn !== undefined) {
            localStorage.setItem('isPromotionalOptIn', String(response.data.isPromotionalOptIn));
          }
          setEditableProfile({
            fullName: response.data.fullName || '',
            email: response.data.email || '',
            contactNumber: response.data.contactNumber || '',
          });

          try {
            const addrResponse = await addressService.getAddress(userId);
            if (addrResponse && addrResponse.success && addrResponse.data) {
              setAddress(addrResponse.data);
              setTempAddress(addrResponse.data);
            }
          } catch (addrErr) {
            console.error('Failed to fetch address:', addrErr);
          }

          // Fetch Wallet Summary from GET /api/wallet/summary
          try {
            setWalletLoading(true);
            const summaryRes = await walletService.getWalletSummary();
            if (summaryRes) {
              setWalletSummary({
                totalBalance: Number(summaryRes.totalBalance ?? summaryRes.balance ?? 0),
                totalCashback: Number(summaryRes.totalCashback ?? summaryRes.totalEarnings ?? 0),
                totalPendingAmount: Number(summaryRes.totalPendingAmount ?? summaryRes.pendingCashback ?? 0),
                totalTransactions: Number(summaryRes.totalTransactions ?? 0),
              });
            }
          } catch (walletErr) {
            console.error('Failed to fetch wallet summary:', walletErr);
          } finally {
            setWalletLoading(false);
          }
        } else {
          setErrorMsg(response.message || 'Failed to load user profile data.');
        }
      } catch (err) {
        setErrorMsg(err.message || 'An error occurred while fetching the profile.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId, isLoggedIn]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setEditableProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setTempAddress((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileUpdateLoading(true);
    setProfileUpdateError('');
    setProfileSuccessMsg('');

    try {
      const payload = {
        id: profile.id,
        userId: profile.userId,
        fullName: editableProfile.fullName,
        email: editableProfile.email,
        contactNumber: editableProfile.contactNumber,
        isActive: profile.isActive,
      };

      const response = await authService.updateUserProfile(payload);
      if (response && response.success) {
        setProfile(response.data);
        setEditableProfile({
          fullName: response.data.fullName || '',
          email: response.data.email || '',
          contactNumber: response.data.contactNumber || '',
        });
        setIsProfileDialogOpen(false);
        setProfileSuccessMsg(response.message || 'Profile updated successfully!');
        setTimeout(() => setProfileSuccessMsg(''), 3000);
      } else {
        setProfileUpdateError(response.message || 'Failed to update profile.');
      }
    } catch (err) {
      setProfileUpdateError(err.message || 'An error occurred while updating the profile.');
    } finally {
      setProfileUpdateLoading(false);
    }
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    setAddressLoading(true);
    setAddressSuccessMsg('');
    setAddressError('');

    try {
      const hasExistingAddress = !!address.id;
      const payload = {
        userId: Number(userId),
        line: tempAddress.line,
        city: tempAddress.city,
        state: tempAddress.state,
        pincode: tempAddress.pincode,
      };

      if (hasExistingAddress) {
        payload.id = address.id;
      }

      const response = hasExistingAddress
        ? await addressService.updateAddress(payload)
        : await addressService.saveAddress(payload);

      if (response && response.success) {
        const savedData = response.data || tempAddress;
        setAddress(savedData);
        setTempAddress(savedData);
        setIsAddressDialogOpen(false);
        setAddressSuccessMsg(response.message || (hasExistingAddress ? 'Address updated successfully!' : 'Address saved successfully!'));
        setTimeout(() => setAddressSuccessMsg(''), 3000);
      } else {
        setAddressError(response.message || `Failed to ${hasExistingAddress ? 'update' : 'save'} address.`);
      }
    } catch (err) {
      setAddressError(err.message || `An error occurred while ${address.id ? 'updating' : 'saving'} the address.`);
    } finally {
      setAddressLoading(false);
    }
  };

  const handleDeleteAddress = () => {
    if (!address.id) return;
    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleteConfirmOpen(false);
    setAddressLoading(true);
    setAddressSuccessMsg('');
    setAddressError('');

    try {
      const response = await addressService.deleteAddress(address.id);
      if (response && response.success) {
        setAddress({
          line: '',
          city: '',
          state: '',
          pincode: '',
        });
        setTempAddress({
          line: '',
          city: '',
          state: '',
          pincode: '',
        });
        setAddressSuccessMsg(response.message || 'Address deleted successfully!');
        setTimeout(() => setAddressSuccessMsg(''), 3000);
      } else {
        setAddressError(response.message || 'Failed to delete address.');
        setTimeout(() => setAddressError(''), 3000);
      }
    } catch (err) {
      setAddressError(err.message || 'An error occurred while deleting the address.');
      setTimeout(() => setAddressError(''), 3000);
    } finally {
      setAddressLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  if (!isLoggedIn) {
    return (
      <Box
        sx={{
          minHeight: 'calc(100vh - 70px)',
          display: 'flex',
          alignItems: 'center',
          justify: 'center',
          padding: { xs: '16px 12px', sm: '24px 16px', md: '48px 24px' },
          position: 'relative',
          background: '#0F2E2B',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: `
              radial-gradient(650px 450px at 85% -10%, rgba(212,162,76,0.14), transparent 60%),
              radial-gradient(550px 450px at -10% 80%, rgba(47,143,118,0.18), transparent 60%)
            `,
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, px: { xs: 1.5, sm: 2 } }}>
          <Paper
            elevation={0}
            sx={{
              background: 'rgba(21, 58, 53, 0.7)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(212, 162, 76, 0.3)',
              borderRadius: { xs: '20px', sm: '28px' },
              padding: { xs: 2.5, sm: 3.5, md: 5 },
              textAlign: 'center',
              boxShadow: '0 12px 40px 0 rgba(0, 0, 0, 0.35)',
              width: '100%',
            }}
          >
            <Box
              sx={{
                width: { xs: 56, sm: 72 },
                height: { xs: 56, sm: 72 },
                borderRadius: '50%',
                backgroundColor: 'rgba(212, 162, 76, 0.15)',
                color: '#D4A24C',
                display: 'grid',
                placeItems: 'center',
                mx: 'auto',
                mb: { xs: 2, sm: 3 },
                border: '1px solid rgba(212, 162, 76, 0.3)',
              }}
            >
              <Lock sx={{ fontSize: { xs: 28, sm: 36 } }} />
            </Box>

            <Typography
              variant="h4"
              sx={{
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                color: '#F5F1E6',
                fontSize: { xs: '20px', sm: '28px' },
                mb: 1.5,
              }}
            >
              Profile Access Restricted
            </Typography>

            <Typography
              sx={{
                color: 'rgba(245, 241, 230, 0.7)',
                fontSize: { xs: '13.5px', sm: '15px' },
                lineHeight: 1.6,
                mb: { xs: 2.5, sm: 4 },
              }}
            >
              No active session found. Please sign in to view and manage your account profile, delivery addresses, and order history.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                onClick={() => onNavigate && onNavigate('login')}
                startIcon={<Person />}
                sx={{
                  backgroundColor: '#D4A24C',
                  color: '#0F2E2B',
                  fontWeight: 700,
                  fontSize: { xs: '13.5px', sm: '15px' },
                  px: { xs: 2.5, sm: 4 },
                  py: { xs: 1.1, sm: 1.4 },
                  borderRadius: '14px',
                  '&:hover': {
                    backgroundColor: '#E8C078',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Sign In to View Profile
              </Button>

              <Button
                variant="outlined"
                onClick={() => onNavigate && onNavigate('signup')}
                sx={{
                  borderColor: 'rgba(245, 241, 230, 0.3)',
                  color: '#F5F1E6',
                  fontWeight: 600,
                  fontSize: { xs: '13.5px', sm: '15px' },
                  px: { xs: 2.5, sm: 3 },
                  py: { xs: 1.1, sm: 1.4 },
                  borderRadius: '14px',
                  '&:hover': {
                    borderColor: '#D4A24C',
                    color: '#D4A24C',
                    backgroundColor: 'rgba(212, 162, 76, 0.08)',
                  },
                }}
              >
                Create Account
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 70px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: '16px 12px', sm: '24px 16px', md: '40px 24px' },
        position: 'relative',
        background: '#0F2E2B',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(650px 450px at 85% -10%, rgba(212,162,76,0.14), transparent 60%),
            radial-gradient(550px 450px at -10% 80%, rgba(47,143,118,0.18), transparent 60%)
          `,
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, px: { xs: 1.5, sm: 2 } }}>
        <Paper
          elevation={0}
          sx={{
            background: 'rgba(21, 58, 53, 0.6)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(245, 241, 230, 0.12)',
            borderRadius: { xs: '20px', sm: '28px' },
            padding: { xs: 2, sm: 3, md: 4 },
            boxShadow: '0 12px 40px 0 rgba(0, 0, 0, 0.35)',
            width: '100%',
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: { xs: 5, md: 8 }, width: '100%' }}>
              <CircularProgress size={50} sx={{ color: '#D4A24C', mb: 2 }} />
              <Typography variant="body2" sx={{ color: 'rgba(245, 241, 230, 0.66)' }}>
                Loading profile details...
              </Typography>
            </Box>
          ) : errorMsg ? (
            <Box sx={{ py: 4, textAlign: 'center', width: '100%' }}>
              <Typography variant="body1" sx={{ color: '#E4572E', mb: 3 }}>
                {errorMsg}
              </Typography>
              {!userId && (
                <Button
                  variant="contained"
                  onClick={() => onNavigate && onNavigate('login')}
                  sx={{
                    backgroundColor: '#D4A24C',
                    color: '#0F2E2B',
                    '&:hover': { backgroundColor: '#E8C078' },
                  }}
                >
                  Go to Login
                </Button>
              )}
            </Box>
          ) : (
            profile && (
              <Stack spacing={{ xs: 2.5, sm: 3.5, md: 4 }}>
                {/* SECTION 1: USER DETAILS (Beside Avatar & Badge) */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 3.5 },
                    alignItems: { xs: 'center', sm: 'flex-start' },
                    width: '100%',
                  }}
                >
                  {/* Left Side: Avatar & Activation Tag */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      minWidth: { xs: 'auto', sm: '120px' },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: { xs: 72, sm: 90 },
                        height: { xs: 72, sm: 90 },
                        fontSize: { xs: '26px', sm: '32px' },
                        fontWeight: 600,
                        color: '#0F2E2B',
                        backgroundColor: '#D4A24C',
                        border: '3px solid rgba(245, 241, 230, 0.12)',
                        boxShadow: '0 0 20px rgba(212, 162, 76, 0.25)',
                        mb: 1,
                        fontFamily: '"Fraunces", serif',
                      }}
                    >
                      {getInitials(profile.fullName)}
                    </Avatar>
                    {profile.isActive && (
                      <Chip
                        icon={<CheckCircle sx={{ '&&': { color: '#2F8F76', fontSize: '14px' } }} />}
                        label="Active"
                        sx={{
                          backgroundColor: 'rgba(47, 143, 118, 0.15)',
                          border: '1px solid #2F8F76',
                          color: '#F5F1E6',
                          fontWeight: 600,
                          fontSize: '11px',
                          px: 0.5,
                        }}
                      />
                    )}
                  </Box>

                  {/* Right Side: Details Form / View */}
                  <Box sx={{ flexGrow: 1, width: '100%' }}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 1.5,
                      mb: { xs: 1.5, sm: 2 }
                    }}>
                      <Typography variant="h6" sx={{ fontFamily: '"Fraunces", serif', color: '#F5F1E6', display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '18px', sm: '20px' } }}>
                        <Person sx={{ color: '#D4A24C' }} /> Personal Details
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="outlined"
                          startIcon={<Edit />}
                          onClick={() => {
                            setEditableProfile({
                              fullName: profile.fullName || '',
                              email: profile.email || '',
                              contactNumber: profile.contactNumber || '',
                            });
                            setProfileUpdateError('');
                            setIsProfileDialogOpen(true);
                          }}
                          size="small"
                          sx={{
                            borderColor: '#D4A24C',
                            color: '#D4A24C',
                            borderRadius: '999px',
                            '&:hover': {
                              borderColor: '#E8C078',
                              backgroundColor: 'rgba(212, 162, 76, 0.05)',
                            },
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<LogoutIcon />}
                          onClick={() => performLogout()}
                          size="small"
                          sx={{
                            borderColor: '#E4572E',
                            color: '#E4572E',
                            borderRadius: '999px',
                            '&:hover': {
                              borderColor: '#E4572E',
                              backgroundColor: 'rgba(228, 87, 46, 0.1)',
                            },
                          }}
                        >
                          Logout
                        </Button>
                      </Stack>
                    </Box>

                    {profileSuccessMsg && (
                      <Box
                        sx={{
                          mb: { xs: 1.5, sm: 2 },
                          p: 1.2,
                          borderRadius: '8px',
                          backgroundColor: 'rgba(47, 143, 118, 0.15)',
                          border: '1px solid #2F8F76',
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#F5F1E6', textAlign: 'center' }}>
                          {profileSuccessMsg}
                        </Typography>
                      </Box>
                    )}

                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ textAlign: 'left' }}>
                          <Typography variant="caption" sx={{ color: 'rgba(245, 241, 230, 0.4)', display: 'block' }}>
                            Full Name
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#F5F1E6', fontWeight: 500 }}>
                            {profile.fullName}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ textAlign: 'left' }}>
                          <Typography variant="caption" sx={{ color: 'rgba(245, 241, 230, 0.4)', display: 'block' }}>
                            Email Address
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#F5F1E6', fontWeight: 500 }}>
                            {profile.email || 'Not Provided'}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{ textAlign: 'left' }}>
                          <Typography variant="caption" sx={{ color: 'rgba(245, 241, 230, 0.4)', display: 'block' }}>
                            Mobile Number
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#F5F1E6', fontWeight: 500 }}>
                            {profile.contactNumber || 'Not Provided'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Edit Profile Dialog Modal */}
                    <Dialog
                      open={isProfileDialogOpen}
                      onClose={() => setIsProfileDialogOpen(false)}
                      maxWidth="sm"
                      fullWidth
                      slotProps={{
                        backdrop: {
                          sx: {
                            backgroundColor: 'rgba(15, 46, 43, 0.6)',
                            backdropFilter: 'blur(4px)',
                          }
                        }
                      }}
                      PaperProps={{
                        sx: {
                          background: '#153A35',
                          border: '1px solid rgba(245, 241, 230, 0.12)',
                          borderRadius: '20px',
                          color: '#F5F1E6',
                          padding: { xs: 1.5, sm: 2 },
                        }
                      }}
                    >
                      <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, pb: 1, fontSize: { xs: '18px', sm: '20px' } }}>
                        Edit Personal Details
                      </DialogTitle>
                      <form onSubmit={handleSaveProfile}>
                        <DialogContent sx={{ pt: 1, px: { xs: 1.5, sm: 3 } }}>
                          {profileUpdateError && (
                            <Box
                              sx={{
                                mb: 2,
                                p: 1.2,
                                borderRadius: '8px',
                                backgroundColor: 'rgba(228, 87, 46, 0.15)',
                                border: '1px solid #E4572E',
                              }}
                            >
                              <Typography variant="body2" sx={{ color: '#F5F1E6', textAlign: 'center' }}>
                                {profileUpdateError}
                              </Typography>
                            </Box>
                          )}
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                              <TextField
                                fullWidth
                                label="Full Name"
                                name="fullName"
                                value={editableProfile.fullName}
                                onChange={handleProfileChange}
                                required
                                sx={inputStyles}
                              />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                              <TextField
                                fullWidth
                                label="Email Address"
                                name="email"
                                value={editableProfile.email}
                                onChange={handleProfileChange}
                                sx={inputStyles}
                              />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                              <TextField
                                fullWidth
                                label="Mobile Number"
                                name="contactNumber"
                                value={editableProfile.contactNumber}
                                onChange={handleProfileChange}
                                sx={inputStyles}
                              />
                            </Grid>
                          </Grid>
                        </DialogContent>
                        <DialogActions sx={{ padding: { xs: '12px 16px', sm: '16px 24px' } }}>
                          <Button
                            onClick={() => setIsProfileDialogOpen(false)}
                            sx={{
                              color: 'rgba(245, 241, 230, 0.6)',
                              '&:hover': { color: '#F5F1E6' }
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            disabled={profileUpdateLoading}
                            variant="contained"
                            sx={{
                              borderRadius: '999px',
                              backgroundColor: '#D4A24C',
                              color: '#0F2E2B',
                              px: 3,
                              fontWeight: 600,
                              '&:hover': {
                                backgroundColor: '#E8C078',
                              },
                            }}
                          >
                            {profileUpdateLoading ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
                          </Button>
                        </DialogActions>
                      </form>
                    </Dialog>
                  </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(245, 241, 230, 0.08)' }} />

                {/* SECTION 2: WALLET INFO (Summary Cards & Actions) */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="h6" sx={{ fontFamily: '"Fraunces", serif', color: '#F5F1E6', display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '18px', sm: '20px' } }}>
                      <AccountBalanceWallet sx={{ color: '#D4A24C' }} /> Wallet Info
                    </Typography>
                    <Button
                      variant="text"
                      endIcon={<CallMade />}
                      onClick={() => onNavigate && onNavigate('wallet')}
                      sx={{ color: '#D4A24C', fontWeight: 600, fontSize: '13px', '&:hover': { color: '#E8C078' } }}
                    >
                      View Full Wallet
                    </Button>
                  </Box>

                  <Box
                    sx={{
                      background: 'rgba(15, 46, 43, 0.4)',
                      border: '1px solid rgba(245, 241, 230, 0.08)',
                      borderRadius: '20px',
                      padding: { xs: 1.5, sm: 2.25, md: 3 },
                    }}
                  >
                    {/* Summary Metrics Grid */}
                    <Grid container spacing={{ xs: 1.25, sm: 1.5, md: 2 }} sx={{ mb: { xs: 1.5, sm: 2 } }}>
                      {/* Total Balance */}
                      <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                        <Box sx={{ p: { xs: 1.25, sm: 1.75 }, borderRadius: '14px', backgroundColor: 'rgba(21, 58, 53, 0.7)', border: '1px solid rgba(212, 162, 76, 0.2)' }}>
                          <Typography variant="caption" sx={{ color: 'rgba(245, 241, 230, 0.5)', display: 'block', mb: 0.5, fontSize: { xs: '11px', sm: '12px' } }}>
                            Total Balance
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#D4A24C', fontWeight: 700, fontFamily: '"Fraunces", serif', fontSize: { xs: '16px', sm: '18px', md: '20px' } }}>
                            {walletLoading ? '...' : `₹${Number(walletSummary.totalBalance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Total Cashback */}
                      <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                        <Box sx={{ p: { xs: 1.25, sm: 1.75 }, borderRadius: '14px', backgroundColor: 'rgba(21, 58, 53, 0.7)', border: '1px solid rgba(245, 241, 230, 0.08)' }}>
                          <Typography variant="caption" sx={{ color: 'rgba(245, 241, 230, 0.5)', display: 'block', mb: 0.5, fontSize: { xs: '11px', sm: '12px' } }}>
                            Total Cashback
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 700, fontFamily: '"Fraunces", serif', fontSize: { xs: '16px', sm: '18px', md: '20px' } }}>
                            {walletLoading ? '...' : `₹${Number(walletSummary.totalCashback).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Pending Amount */}
                      <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                        <Box sx={{ p: { xs: 1.25, sm: 1.75 }, borderRadius: '14px', backgroundColor: 'rgba(21, 58, 53, 0.7)', border: '1px solid rgba(245, 241, 230, 0.08)' }}>
                          <Typography variant="caption" sx={{ color: 'rgba(245, 241, 230, 0.5)', display: 'block', mb: 0.5, fontSize: { xs: '11px', sm: '12px' } }}>
                            Pending Amount
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#FFB74D', fontWeight: 700, fontFamily: '"Fraunces", serif', fontSize: { xs: '16px', sm: '18px', md: '20px' } }}>
                            {walletLoading ? '...' : `₹${Number(walletSummary.totalPendingAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Total Transactions */}
                      <Grid size={{ xs: 6, sm: 3, md: 3 }}>
                        <Box sx={{ p: { xs: 1.25, sm: 1.75 }, borderRadius: '14px', backgroundColor: 'rgba(21, 58, 53, 0.7)', border: '1px solid rgba(245, 241, 230, 0.08)' }}>
                          <Typography variant="caption" sx={{ color: 'rgba(245, 241, 230, 0.5)', display: 'block', mb: 0.5, fontSize: { xs: '11px', sm: '12px' } }}>
                            Total Transactions
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#F5F1E6', fontWeight: 700, fontFamily: '"Fraunces", serif', fontSize: { xs: '16px', sm: '18px', md: '20px' } }}>
                            {walletLoading ? '...' : `${walletSummary.totalTransactions}`}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(245, 241, 230, 0.08)' }} />

                {/* SECTION 3: ADDRESS DETAILS */}
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: { xs: 1.5, sm: 2 },
                    }}
                  >
                    <Typography variant="h6" sx={{ fontFamily: '"Fraunces", serif', color: '#F5F1E6', display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '18px', sm: '20px' } }}>
                      <LocationOn sx={{ color: '#D4A24C' }} /> Address Details
                    </Typography>
                    {address.line && address.city && address.state && address.pincode ? (
                      <Button
                        variant="outlined"
                        startIcon={<Edit />}
                        size="small"
                        onClick={() => {
                          setTempAddress(address);
                          setIsAddressDialogOpen(true);
                        }}
                        sx={{
                          borderColor: '#D4A24C',
                          color: '#D4A24C',
                          borderRadius: '999px',
                          '&:hover': {
                            borderColor: '#E8C078',
                            backgroundColor: 'rgba(212, 162, 76, 0.05)',
                          },
                        }}
                      >
                        Edit
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        size="small"
                        onClick={() => {
                          setTempAddress({ line: '', city: '', state: '', pincode: '' });
                          setIsAddressDialogOpen(true);
                        }}
                        sx={{
                          borderColor: '#D4A24C',
                          color: '#D4A24C',
                          borderRadius: '999px',
                          '&:hover': {
                            borderColor: '#E8C078',
                            backgroundColor: 'rgba(212, 162, 76, 0.05)',
                          },
                        }}
                      >
                        Add
                      </Button>
                    )}
                  </Box>

                  {addressSuccessMsg && (
                    <Box
                      sx={{
                        mb: { xs: 1.5, sm: 2 },
                        p: 1.2,
                        borderRadius: '8px',
                        backgroundColor: 'rgba(47, 143, 118, 0.15)',
                        border: '1px solid #2F8F76',
                      }}
                    >
                      <Typography variant="body2" sx={{ color: '#F5F1E6', textAlign: 'center' }}>
                        {addressSuccessMsg}
                      </Typography>
                    </Box>
                  )}

                  {address.line && address.city && address.state && address.pincode ? (
                    <Box
                      sx={{
                        background: 'rgba(15, 46, 43, 0.4)',
                        border: '1px solid rgba(245, 241, 230, 0.08)',
                        borderRadius: '16px',
                        padding: { xs: 1.5, sm: 2.5 },
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flexGrow: 1 }}>
                        <Typography variant="body1" sx={{ color: '#F5F1E6', fontWeight: 500, fontSize: { xs: '14px', sm: '16px' } }}>
                          {address.line}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(245, 241, 230, 0.6)', fontSize: { xs: '13px', sm: '14px' } }}>
                          {address.city}, {address.state} - {address.pincode}
                        </Typography>
                      </Box>
                      <IconButton
                        onClick={handleDeleteAddress}
                        disabled={addressLoading}
                        sx={{
                          color: '#E4572E',
                          border: '1px solid rgba(228, 87, 46, 0.3)',
                          borderRadius: '999px',
                          padding: '8px',
                          alignSelf: 'center',
                          '&:hover': {
                            backgroundColor: 'rgba(228, 87, 46, 0.1)',
                            borderColor: '#E4572E',
                          },
                        }}
                      >
                        {addressLoading ? <CircularProgress size={20} color="inherit" /> : <Delete fontSize="small" />}
                      </IconButton>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        background: 'rgba(15, 46, 43, 0.2)',
                        border: '1px dashed rgba(245, 241, 230, 0.12)',
                        borderRadius: '16px',
                        padding: { xs: 2.5, sm: 3.5 },
                        textAlign: 'center',
                      }}
                    >
                      <LocationOn sx={{ color: 'rgba(245, 241, 230, 0.2)', fontSize: '36px', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: 'rgba(245, 241, 230, 0.5)', fontSize: { xs: '13px', sm: '14px' } }}>
                        No address added yet. Please click "Add Address" to provide your delivery details.
                      </Typography>
                    </Box>
                  )}

                  {/* Add / Update Address Popup Dialog */}
                  <Dialog
                    open={isAddressDialogOpen}
                    onClose={() => setIsAddressDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    slotProps={{
                      backdrop: {
                        sx: {
                          backgroundColor: 'rgba(15, 46, 43, 0.6)',
                          backdropFilter: 'blur(4px)',
                        }
                      }
                    }}
                    PaperProps={{
                      sx: {
                        background: '#153A35',
                        border: '1px solid rgba(245, 241, 230, 0.12)',
                        borderRadius: '20px',
                        color: '#F5F1E6',
                        padding: { xs: 1.5, sm: 2 },
                      }
                    }}
                  >
                    <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, pb: 1 }}>
                      {address.line ? 'Edit Address Details' : 'Add New Address'}
                    </DialogTitle>
                    <form onSubmit={handleSaveAddress}>
                      <DialogContent sx={{ pt: 1 }}>
                        {addressError && (
                          <Box
                            sx={{
                              mb: 2.5,
                              p: 1.5,
                              borderRadius: '8px',
                              backgroundColor: 'rgba(228, 87, 46, 0.15)',
                              border: '1px solid #E4572E',
                            }}
                          >
                            <Typography variant="body2" sx={{ color: '#F5F1E6', textAlign: 'center' }}>
                              {addressError}
                            </Typography>
                          </Box>
                        )}
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, md: 12 }}>
                            <TextField
                              fullWidth
                              label="Address Line"
                              name="line"
                              value={tempAddress.line}
                              onChange={handleAddressChange}
                              required
                              placeholder="House No, Street name, Area"
                              sx={inputStyles}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                              fullWidth
                              label="City"
                              name="city"
                              value={tempAddress.city}
                              onChange={handleAddressChange}
                              required
                              placeholder="City"
                              sx={inputStyles}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                              fullWidth
                              label="State"
                              name="state"
                              value={tempAddress.state}
                              onChange={handleAddressChange}
                              required
                              placeholder="State"
                              sx={inputStyles}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                              fullWidth
                              label="Pincode"
                              name="pincode"
                              value={tempAddress.pincode}
                              onChange={handleAddressChange}
                              required
                              placeholder="Pincode"
                              sx={inputStyles}
                            />
                          </Grid>
                        </Grid>
                      </DialogContent>
                      <DialogActions sx={{ padding: { xs: '12px 16px', sm: '16px 24px' } }}>
                        <Button
                          onClick={() => setIsAddressDialogOpen(false)}
                          sx={{
                            color: 'rgba(245, 241, 230, 0.6)',
                            '&:hover': { color: '#F5F1E6' }
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          disabled={addressLoading}
                          variant="contained"
                          sx={{
                            borderRadius: '999px',
                            backgroundColor: '#D4A24C',
                            color: '#0F2E2B',
                            px: 3,
                            fontWeight: 600,
                            '&:hover': {
                              backgroundColor: '#E8C078',
                            },
                          }}
                        >
                          {addressLoading ? <CircularProgress size={20} color="inherit" /> : (address.id ? 'Update' : 'Save Address')}
                        </Button>
                      </DialogActions>
                    </form>
                  </Dialog>

                  {/* Delete Address Confirmation Dialog */}
                  <Dialog
                    open={isDeleteConfirmOpen}
                    onClose={() => setIsDeleteConfirmOpen(false)}
                    maxWidth="xs"
                    fullWidth
                    slotProps={{
                      backdrop: {
                        sx: {
                          backgroundColor: 'rgba(15, 46, 43, 0.6)',
                          backdropFilter: 'blur(4px)',
                        }
                      }
                    }}
                    PaperProps={{
                      sx: {
                        background: '#153A35',
                        border: '1px solid rgba(245, 241, 230, 0.12)',
                        borderRadius: '20px',
                        color: '#F5F1E6',
                        padding: { xs: 1.5, sm: 2 },
                      }
                    }}
                  >
                    <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, pb: 1 }}>
                      Confirm Deletion
                    </DialogTitle>
                    <DialogContent sx={{ pt: 1 }}>
                      <Typography variant="body1" sx={{ color: 'rgba(245, 241, 230, 0.8)' }}>
                        Are you sure you want to delete the address?
                      </Typography>
                    </DialogContent>
                    <DialogActions sx={{ padding: { xs: '12px 16px', sm: '16px 24px' } }}>
                      <Button
                        onClick={() => setIsDeleteConfirmOpen(false)}
                        sx={{
                          color: 'rgba(245, 241, 230, 0.6)',
                          '&:hover': { color: '#F5F1E6' }
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        sx={{
                          borderRadius: '999px',
                          backgroundColor: '#E4572E',
                          color: '#F5F1E6',
                          px: 3,
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: '#ff6b42',
                          },
                        }}
                      >
                        Yes
                      </Button>
                    </DialogActions>
                  </Dialog>
                </Box>
              </Stack>
            )
          )}
        </Paper>
      </Container>
    </Box>
  );
}

const inputStyles = {
  '& .MuiInputLabel-root': {
    color: 'rgba(245, 241, 230, 0.5)',
    '&.Mui-focused': {
      color: '#D4A24C',
    },
    '&.Mui-error': {
      color: '#E4572E',
    },
  },
  '& .MuiOutlinedInput-root': {
    color: '#F5F1E6',
    backgroundColor: 'rgba(15, 46, 43, 0.4)',
    borderRadius: '12px',
    transition: 'all 0.2s ease',
    '& fieldset': {
      borderColor: 'rgba(245, 241, 230, 0.12)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(245, 241, 230, 0.3)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#D4A24C',
      borderWidth: '1.5px',
    },
    '&.Mui-error fieldset': {
      borderColor: '#E4572E',
    },
  },
  '& .MuiFormHelperText-root': {
    color: 'rgba(245, 241, 230, 0.5)',
    '&.Mui-error': {
      color: '#E4572E',
      marginLeft: '4px',
    },
  },
};
